import type { InboxItem } from '@erp/shared';
import { useQuery } from '@tanstack/react-query';
import { Link, useNavigate, useSearch } from '@tanstack/react-router';
import { CheckCircle2 } from 'lucide-react';
import { ErrorBox, PageHeader, Segmented, Spinner } from '@/components/ui';
import { formatMoment, plural } from '@/lib/format';
import { inboxQuery } from '@/lib/queries';

export interface ApprovalsSearch {
  state?: 'done';
}

/** Все задания по маршрутам в одном месте: согласовать отпуск, оплатить счёт, проверить документы. */
export function ApprovalsPage() {
  const search = useSearch({ strict: false }) as ApprovalsSearch;
  const navigate = useNavigate();
  const state = search.state ?? 'open';
  const q = useQuery(inboxQuery(state));
  const open = state === 'open' ? q.data?.length : undefined;

  return (
    <div className="max-w-[980px]">
      <PageHeader
        title="Согласования"
        description={
          open === undefined
            ? 'Задания, которые ждут вашего решения'
            : open
              ? `${open} ${plural(open, ['задание ждёт', 'задания ждут', 'заданий ждут'])} вашего решения`
              : 'Все задания выполнены'
        }
      />
      <div className="mb-4">
        <Segmented
          label="Задания"
          value={state}
          options={[
            { value: 'open', label: 'Ждут решения' },
            { value: 'done', label: 'Выполненные' },
          ]}
          onChange={(v) =>
            navigate({
              to: '/approvals',
              search: { state: v === 'done' ? 'done' : undefined },
              replace: true,
            })
          }
        />
      </div>
      {q.isPending ? (
        <Spinner />
      ) : q.error ? (
        <ErrorBox error={q.error} />
      ) : q.data.length === 0 ? (
        <div className="panel flex flex-col items-center gap-2 px-6 py-12 text-center">
          <CheckCircle2 className="size-7 text-ok" aria-hidden />
          <p className="font-medium">
            {state === 'open' ? 'Нет заданий, ожидающих решения' : 'Пока ничего не выполнено'}
          </p>
          <p className="text-steel">
            Когда кто-то подаст заявление или заявку на ваше согласование, оно появится здесь.
          </p>
        </div>
      ) : (
        <ul className="panel divide-y divide-line">
          {q.data.map((i) => (
            <InboxRow key={i.taskId} item={i} />
          ))}
        </ul>
      )}
    </div>
  );
}

function InboxRow({ item }: { item: InboxItem }) {
  return (
    <li>
      <Link
        to={item.link}
        className="flex flex-col gap-0.5 px-5 py-3 hover:bg-paper/70 sm:flex-row sm:items-center sm:gap-4"
      >
        <div className="min-w-0 flex-1">
          <p className="text-[12.5px] text-steel">
            {item.routeTitle} №{item.entityNumber} · {item.author.fullName}
          </p>
          <p className="truncate font-medium">{item.entityTitle}</p>
        </div>
        <div className="flex shrink-0 items-center gap-3 sm:flex-col sm:items-end sm:gap-0.5">
          <span className="text-[13.5px] font-medium">{item.title}</span>
          <span className="text-[12.5px] text-steel">{formatMoment(item.createdAt)}</span>
        </div>
      </Link>
    </li>
  );
}
